import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import { GlobalKPICards, SyndicatorKPICards } from '../components/syndication/KPICards';
import SyndicatorsTable from '../components/syndication/SyndicatorsTable';
import SyndicationDealsTable from '../components/syndication/DealsTable';
import WithdrawalHistoryTable from '../components/syndication/WithdrawalHistoryTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SyndicatedDeals = () => {
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [selectedSyndicator, setSelectedSyndicator] = useState(null);
  const [deals, setDeals] = useState([]);
  const [withdrawalAmounts, setWithdrawalAmounts] = useState({});
  const [withdrawalLedger, setWithdrawalLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deals');
  const [summarySort, setSummarySort] = useState({ column: 'syndicator_name', direction: 'asc' });
  const [dealsSort, setDealsSort] = useState({ column: 'deal_name', direction: 'asc' });
  const [withdrawalsSort, setWithdrawalsSort] = useState({ column: 'date', direction: 'desc' });

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/syndications/summary`, {
        headers: getAuthHeaders(),
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch syndication summary:', error);
      toast.error('Failed to load syndication data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleSelectSyndicator = async (syndicatorSummary) => {
    setSelectedSyndicator(syndicatorSummary);
    setDeals(syndicatorSummary.deals || []);
    
    const initialAmounts = {};
    syndicatorSummary.deals.forEach(deal => {
      initialAmounts[deal.deal_id] = '';
    });
    setWithdrawalAmounts(initialAmounts);

    try {
      const res = await axios.get(`${API}/withdrawals`, {
        headers: getAuthHeaders(),
        params: { syndicator_id: syndicatorSummary.syndicator_id },
      });
      setWithdrawalLedger(res.data || []);
    } catch (err) {
      console.error('Failed to load withdrawal ledger', err);
    }
  };

  const handleWithdrawalAmountChange = (dealId, value) => {
    setWithdrawalAmounts(prev => ({ ...prev, [dealId]: value }));
  };

  const handleWithdraw = async (dealId) => {
    const amount = parseFloat(withdrawalAmounts[dealId]);
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid withdrawal amount');
      return;
    }

    const deal = deals.find(d => d.deal_id === dealId);
    if (amount > deal.balance) {
      toast.error(`Amount exceeds available balance: ${formatCurrency(deal.balance)}`);
      return;
    }

    try {
      await axios.post(
        `${API}/withdrawals`,
        {
          syndicator_id: selectedSyndicator.syndicator_id,
          deal_id: dealId,
          amount: amount,
          notes: `Withdrawal request for deal ${deal.deal_name}`
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Withdrawal request for ${formatCurrency(amount)} submitted`);
      handleWithdrawalAmountChange(dealId, '');
      fetchSummary();
      
      // Refresh syndicator data
      const updatedSummary = await axios.get(`${API}/syndications/summary`, { headers: getAuthHeaders() });
      const updatedSyndicator = updatedSummary.data.find(s => s.syndicator_id === selectedSyndicator.syndicator_id);
      if (updatedSyndicator) {
        handleSelectSyndicator(updatedSyndicator);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal request failed');
    }
  };

  const handleSort = (setSort) => (column) => {
    setSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data, sortConfig) => {
    if (!data || !sortConfig.column) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.column];
      let bVal = b[sortConfig.column];
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const calculateTotals = (deals) => {
    return {
      advanceAmount: deals.reduce((sum, d) => sum + (d.advance_amount || 0), 0),
      targetCollection: deals.reduce((sum, d) => sum + (d.target_collection || 0), 0),
      runningCollection: deals.reduce((sum, d) => sum + (d.running_collection || 0), 0),
      withdrawals: deals.reduce((sum, d) => sum + (d.withdrawals || 0), 0),
      balance: deals.reduce((sum, d) => sum + (d.balance || 0), 0),
      withdrawalAmount: Object.values(withdrawalAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
    };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // Syndicator detail view
  if (selectedSyndicator) {
    const sortedDeals = sortData(deals, dealsSort);
    const sortedWithdrawals = sortData(withdrawalLedger, withdrawalsSort);
    const totals = calculateTotals(deals);

    return (
      <Layout>
        <div className="px-4 sm:px-0">
          {/* Back button and header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => setSelectedSyndicator(null)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Syndicators
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">
              {selectedSyndicator.syndicator_name}
            </h1>
          </div>

          {/* KPI Cards */}
          <SyndicatorKPICards syndicator={selectedSyndicator} />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="deals">Deals</TabsTrigger>
              <TabsTrigger value="withdrawals">Withdrawal History</TabsTrigger>
            </TabsList>

            <TabsContent value="deals">
              <Card>
                <CardContent className="p-4">
                  <SyndicationDealsTable
                    deals={sortedDeals}
                    totals={totals}
                    sortConfig={dealsSort}
                    onSort={handleSort(setDealsSort)}
                    withdrawalAmounts={withdrawalAmounts}
                    onWithdrawalAmountChange={handleWithdrawalAmountChange}
                    onWithdraw={handleWithdraw}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="withdrawals">
              <Card>
                <CardContent className="p-4">
                  <WithdrawalHistoryTable
                    withdrawals={sortedWithdrawals}
                    deals={deals}
                    sortConfig={withdrawalsSort}
                    onSort={handleSort(setWithdrawalsSort)}
                    onRefresh={() => handleSelectSyndicator(selectedSyndicator)}
                    isAdmin={user?.roles?.includes('admin')}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </Layout>
    );
  }

  // Main syndicators list view
  const sortedSummary = sortData(summary || [], summarySort);

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Syndication Withdrawals</h1>

        {/* Syndicators Table */}
        <Card>
          <CardContent className="p-4">
            <SyndicatorsTable
              syndicators={sortedSummary}
              sortConfig={summarySort}
              onSort={handleSort(setSummarySort)}
              onSelectSyndicator={handleSelectSyndicator}
              onWithdraw={(syn) => handleSelectSyndicator(syn)}
              isAdmin={user?.roles?.includes('admin')}
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SyndicatedDeals;
